/**
 * ``usePreflight`` — UI bridge for the "Vérifier tout" flow (THE-451
 * PR-3c).
 *
 * Orchestrates three layers in a single hook so the pane stays
 * presentational:
 *
 *   1. Read every chip in the Word doc via ``citationStore.list()``
 *      (ContentControl adapter, PR-3a).
 *   2. POST the de-duplicated refKeys to ``preflight.run()`` (PR-3c).
 *   3. Reconcile chip status colours in the doc via
 *      ``citationStore.updateStatusBatch()`` — every refKey that landed
 *      in ``ok`` becomes ``verified``, ``stale`` becomes ``stale``,
 *      ``broken`` becomes ``broken-anchor``. One ``Word.run`` round-trip
 *      for the whole batch.
 *
 * The hook exposes ``{ loading, error, report, run }``. The pane
 * triggers preflight on mount AND on the "Vérifier maintenant" button.
 * ``error`` is the typed ``PreflightHttpError`` (kind discriminant), so
 * the pane can map 401 → re-login prompt, 5xx → retry banner, etc.
 *
 * W1 honoured: the hook is pure orchestration glue. No status / bucket
 * classification logic lives here — the BE owns it. The chip → BE
 * status mapping is a 1:1 vocabulary translation, not a decision.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { getContainer } from '@/container'
import { useCaseStore } from '@/ui/stores/use-case-store'
import {
  emptyPreflightReport,
  type PreflightReport,
} from '@/domain/preflight'
import type {
  ChipStatus,
  CitationChip,
} from '@/domain/citation-chip/entity'
import type {
  BordereauValidation,
  RefStatusKind,
} from '@/domain/case-refs/entity'

/** Pièce-bordereau summary for the ribbon (« N ok · M à promouvoir · K
 *  orphelines »). Distinct from the article PreflightReport because the
 *  BE buckets differ (ok / not_promoted / orphan, not ok / stale /
 *  broken). ``uncitedPromoted`` is informational (cover pièces). */
export interface PieceReport {
  ok: number
  notPromoted: number
  orphan: number
  uncitedPromoted: number
}

export function emptyPieceReport(): PieceReport {
  return { ok: 0, notPromoted: 0, orphan: 0, uncitedPromoted: 0 }
}

/** Pièce bordereau status → chip status (1:1 vocabulary translation, same
 *  class as ``bucketStatus``). ``ok`` = cited + in the bordereau; pièces
 *  are binary (in-bordereau or not), never fuzzy-scored — the score is the
 *  band sentinel, not a fabricated confidence (W7). */
function pieceChipStatus(kind: RefStatusKind): ChipStatus {
  switch (kind) {
    case 'ok':
      return 'verified'
    case 'not_promoted':
      return 'unverified'
    case 'orphan':
      return 'broken-anchor'
  }
}

/** Extract the exhibit UUID from a ``piece:{uuid}`` refKey, or ``null``
 *  when the chip is not a pièce. */
function pieceRefIdOf(refKey: string): string | null {
  return refKey.startsWith('piece:') ? refKey.slice('piece:'.length) : null
}

/** Build the chip-status batch for the pièce chips from a bordereau
 *  validation. Each piece chip's refId is matched to a BE status; ``ok``
 *  → verified (band 1.0), everything else → 0 (no fuzzy score for pièces). */
function buildPieceBatchUpdates(
  pieceChips: readonly CitationChip[],
  validation: BordereauValidation,
): Array<{ cid: string; status: ChipStatus; score: number }> {
  const statusByRefId = new Map(
    validation.statuses.map((s) => [s.refId, s.status]),
  )
  const updates: Array<{ cid: string; status: ChipStatus; score: number }> = []
  for (const chip of pieceChips) {
    const refId = pieceRefIdOf(chip.refKey)
    if (refId === null) continue
    const kind = statusByRefId.get(refId)
    if (kind === undefined) continue
    updates.push({
      cid: chip.cid,
      status: pieceChipStatus(kind),
      score: kind === 'ok' ? 1 : 0,
    })
  }
  return updates
}

function summarizePiece(validation: BordereauValidation): PieceReport {
  const r = emptyPieceReport()
  for (const s of validation.statuses) {
    if (s.status === 'ok') r.ok += 1
    else if (s.status === 'not_promoted') r.notPromoted += 1
    else r.orphan += 1
  }
  r.uncitedPromoted = validation.uncitedPromoted.length
  return r
}

/** Bucket → chip status vocabulary translation. The BE buckets
 *  (ok / stale / broken) map 1:1 to the chip status enum the Office
 *  binding stores. This is the only place where the mapping lives so a
 *  vocabulary change on either side breaks here loudly.
 *
 *  THE-488 (Medium) — practising-lawyer sign-off pending on the
 *  ``stale`` mapping. Whether ``stale`` should land as ``'stale'``
 *  ("repealed/amended, re-verify") or ``'unverified'`` (the weaker
 *  tier) is a legal-convention call. PR-9 handles it. */
function bucketStatus(bucket: 'ok' | 'stale' | 'broken'): ChipStatus {
  switch (bucket) {
    case 'ok':
      return 'verified'
    case 'stale':
      return 'stale'
    case 'broken':
      return 'broken-anchor'
  }
}

/** Verdict shape kept narrow — score is computed per-chip at fan-out
 *  time for the ``stale`` bucket (the BE wire carries no score for
 *  stale entries; we preserve the chip's previous score verbatim
 *  rather than inventing a sentinel — W7 invariant). */
type Verdict =
  | { bucket: 'ok'; score: number }
  | { bucket: 'stale' }
  | { bucket: 'broken' }

/** Build the batch-update payload from a preflight report. Each refKey
 *  may correspond to many chips (one article cited in N paragraphs);
 *  every chip with that refKey gets the same status. Score follows the
 *  W7 rule — BE-sourced when present, otherwise carry over the chip's
 *  previous value (no client-side fabrication). */
function buildBatchUpdates(
  chips: readonly CitationChip[],
  report: PreflightReport,
): Array<{ cid: string; status: ChipStatus; score: number }> {
  const verdictByRefKey = new Map<string, Verdict>()
  for (const ok of report.ok) {
    verdictByRefKey.set(ok.refKey, { bucket: 'ok', score: ok.score })
  }
  for (const stale of report.stale) {
    verdictByRefKey.set(stale.refKey, { bucket: 'stale' })
  }
  for (const broken of report.broken) {
    verdictByRefKey.set(broken.refKey, { bucket: 'broken' })
  }
  const updates: Array<{ cid: string; status: ChipStatus; score: number }> = []
  for (const chip of chips) {
    const verdict = verdictByRefKey.get(chip.refKey)
    if (!verdict) continue
    const status = bucketStatus(verdict.bucket)
    // Score sourcing rule (W7):
    //   - ok → BE-supplied PreflightOkEntry.score
    //   - stale → carry over the chip's previous score (the BE wire
    //     carries no score for stale entries; inventing one client-side
    //     would be a W7 leak. The chip stays at its prior gold-band
    //     until BE confirms re-verification.)
    //   - broken → 0 (the refKey is unresolvable — there is no
    //     confidence to score, so the chip flatlines to the broken
    //     colour band)
    const score =
      verdict.bucket === 'ok'
        ? verdict.score
        : verdict.bucket === 'stale'
          ? chip.score
          : 0
    updates.push({ cid: chip.cid, status, score })
  }
  return updates
}

export interface UsePreflightResult {
  loading: boolean
  error: Error | null
  report: PreflightReport
  /** Pièce-bordereau summary (F1 PR-5). Populated when a case is active +
   *  the doc has pièce chips; otherwise zeroed. */
  pieceReport: PieceReport
  /** Manually re-run preflight (button "Vérifier maintenant"). The
   *  mount-time auto-run is independent — the hook re-runs whenever
   *  ``run()`` is called explicitly. */
  run: () => Promise<void>
}

export interface UsePreflightOptions {
  /** Run preflight automatically on mount. Default ``true``; the live
   *  SourcesPane passes ``false`` so opening the Sources tab doesn't burn a
   *  preflight network call + rate-limit hit on every mount — there preflight
   *  runs only on the explicit « Vérifier tout » button. */
  autoRun?: boolean
}

export function usePreflight(
  options: UsePreflightOptions = {},
): UsePreflightResult {
  const { autoRun = true } = options
  // When not auto-running, the hook starts idle (no flight pending).
  const [loading, setLoading] = useState<boolean>(autoRun)
  const [error, setError] = useState<Error | null>(null)
  const [report, setReport] = useState<PreflightReport>(emptyPreflightReport)
  const [pieceReport, setPieceReport] = useState<PieceReport>(emptyPieceReport)
  const inFlightRef = useRef<boolean>(false)

  const run = useCallback(async () => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setLoading(true)
    setError(null)
    try {
      const { citationStore, preflightRepository, caseRefs } = getContainer()
      const allChips = await citationStore.list()
      // Two verify paths by design: article/ECLI chips → the corpus-scoped
      // ``citation-registry/preflight`` (authority lookup); pièce chips →
      // the case-scoped ``cases/{id}/refs/validate`` (bordereau check). A
      // ``piece:`` refKey would be mis-bucketed ``broken`` by preflight, so
      // the two never mix.
      const articleChips = allChips.filter(
        (c) => c.kind === 'article' || c.kind === 'jp',
      )
      const pieceChips = allChips.filter((c) => c.kind === 'piece')

      // ── Article / ECLI path ──
      // De-dupe refKeys before sending — two chips citing the same
      // article would otherwise double-charge the BE batch quota.
      const refKeys = Array.from(new Set(articleChips.map((c) => c.refKey)))
      const nextReport =
        refKeys.length > 0
          ? await preflightRepository.run(refKeys)
          : emptyPreflightReport()
      const articleUpdates =
        refKeys.length > 0 ? buildBatchUpdates(articleChips, nextReport) : []

      // ── Pièce / bordereau path (F1 PR-5) ──
      // Gated on an active case: a pièce chip is meaningless without the
      // case whose bordereau it belongs to. ``activeContext`` is read at
      // call time (not a hook dep) — same pattern as use-cite.
      const active = useCaseStore.getState().activeContext
      let nextPieceReport = emptyPieceReport()
      let pieceUpdates: Array<{
        cid: string
        status: ChipStatus
        score: number
      }> = []
      if (active && pieceChips.length > 0) {
        const refIds = Array.from(
          new Set(
            pieceChips
              .map((c) => pieceRefIdOf(c.refKey))
              .filter((id): id is string => id !== null),
          ),
        )
        const validation = await caseRefs.validate(active.caseId, refIds)
        pieceUpdates = buildPieceBatchUpdates(pieceChips, validation)
        nextPieceReport = summarizePiece(validation)
      }

      // One Office round-trip for both paths.
      const batch = [...articleUpdates, ...pieceUpdates]
      if (batch.length > 0) {
        await citationStore.updateStatusBatch(batch)
      }
      setReport(nextReport)
      setPieceReport(nextPieceReport)
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err))
      // eslint-disable-next-line no-console
      console.error('[usePreflight] failed', wrapped)
      setError(wrapped)
    } finally {
      setLoading(false)
      inFlightRef.current = false
    }
  }, [])

  // Auto-run on mount (unless opted out). Pane unmount during a flight
  // is OK — we read through `inFlightRef` to no-op a concurrent click.
  useEffect(() => {
    if (autoRun) void run()
  }, [autoRun, run])

  return { loading, error, report, pieceReport, run }
}
